<script setup>
// Секция «MCP-серверы» на странице инструментов (Р9.4): подключение по
// адресу (+ Bearer-токен), инструменты сервера подтягиваются кнопкой
// «Обновить инструменты» и подключаются к агентам в их формах.
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AssistantMcpServersAPI from 'dashboard/api/assistant/mcpServers';

const { t } = useI18n();

const servers = ref([]);
const isFetching = ref(false);
const syncingIds = ref(new Set());
const showAddForm = ref(false);

const form = reactive({
  name: '',
  url: '',
  authToken: '',
  isSaving: false,
});

const fetchServers = async () => {
  isFetching.value = true;
  try {
    const { data } = await AssistantMcpServersAPI.get();
    servers.value = data.payload || [];
  } catch (error) {
    servers.value = [];
  } finally {
    isFetching.value = false;
  }
};

const replaceServer = updated => {
  servers.value = servers.value.map(server =>
    server.id === updated.id ? updated : server
  );
};

const syncServer = async server => {
  syncingIds.value = new Set([...syncingIds.value, server.id]);
  try {
    const { data } = await AssistantMcpServersAPI.sync(server.id);
    replaceServer(data);
    useAlert(
      t('ASSISTANT.TOOLS.MCP.SYNC_SUCCESS', { count: data.tools_count || 0 })
    );
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      error?.message ||
      t('ASSISTANT.TOOLS.MCP.SYNC_ERROR');
    useAlert(message);
    fetchServers();
  } finally {
    const next = new Set(syncingIds.value);
    next.delete(server.id);
    syncingIds.value = next;
  }
};

const addServer = async () => {
  if (!form.name.trim() || !form.url.trim()) return;
  form.isSaving = true;
  try {
    const { data } = await AssistantMcpServersAPI.create({
      name: form.name.trim(),
      url: form.url.trim(),
      auth_token: form.authToken.trim() || null,
    });
    servers.value = [data, ...servers.value];
    form.name = '';
    form.url = '';
    form.authToken = '';
    showAddForm.value = false;
    useAlert(t('ASSISTANT.TOOLS.MCP.ADD_SUCCESS'));
    syncServer(data);
  } catch (error) {
    useAlert(error?.message || t('ASSISTANT.TOOLS.MCP.ADD_ERROR'));
  } finally {
    form.isSaving = false;
  }
};

const toggleEnabled = async server => {
  try {
    const { data } = await AssistantMcpServersAPI.update(server.id, {
      enabled: !server.enabled,
    });
    replaceServer(data);
  } catch (error) {
    useAlert(error?.message || t('ASSISTANT.TOOLS.MCP.UPDATE_ERROR'));
  }
};

const removeServer = async server => {
  try {
    await AssistantMcpServersAPI.delete(server.id);
    servers.value = servers.value.filter(item => item.id !== server.id);
    useAlert(t('ASSISTANT.TOOLS.MCP.REMOVE_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('ASSISTANT.TOOLS.MCP.REMOVE_ERROR'));
  }
};

onMounted(fetchServers);
</script>

<template>
  <section class="flex flex-col gap-3 mt-8">
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('ASSISTANT.TOOLS.MCP.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('ASSISTANT.TOOLS.MCP.DESCRIPTION') }}
        </p>
      </div>
      <Button
        type="button"
        :label="t('ASSISTANT.TOOLS.MCP.ADD_BUTTON')"
        icon="i-lucide-plus"
        size="sm"
        @click="showAddForm = !showAddForm"
      />
    </div>

    <div
      v-if="showAddForm"
      class="flex flex-col gap-2 p-4 rounded-xl border border-n-weak"
    >
      <Input
        v-model="form.name"
        :label="t('ASSISTANT.TOOLS.MCP.FORM.NAME_LABEL')"
        :placeholder="t('ASSISTANT.TOOLS.MCP.FORM.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="form.url"
        :label="t('ASSISTANT.TOOLS.MCP.FORM.URL_LABEL')"
        placeholder="https://mcp.example.com/mcp"
        :message="t('ASSISTANT.TOOLS.MCP.FORM.URL_HELP')"
      />
      <Input
        v-model="form.authToken"
        type="password"
        autocomplete="new-password"
        :label="t('ASSISTANT.TOOLS.MCP.FORM.TOKEN_LABEL')"
        :message="t('ASSISTANT.TOOLS.MCP.FORM.TOKEN_HELP')"
      />
      <div class="flex justify-end gap-2">
        <Button
          type="button"
          :label="t('ASSISTANT.FORM.CANCEL')"
          variant="faded"
          color="slate"
          size="sm"
          @click="showAddForm = false"
        />
        <Button
          type="button"
          :label="t('ASSISTANT.TOOLS.MCP.FORM.SUBMIT')"
          size="sm"
          :is-loading="form.isSaving"
          :disabled="!form.name.trim() || !form.url.trim()"
          @click="addServer"
        />
      </div>
    </div>

    <p v-if="!isFetching && !servers.length" class="text-sm text-n-slate-11">
      {{ t('ASSISTANT.TOOLS.MCP.EMPTY') }}
    </p>

    <div
      v-for="server in servers"
      :key="server.id"
      class="flex flex-col gap-2 p-4 rounded-xl border border-n-weak"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ server.name }}
          </span>
          <span class="text-xs truncate text-n-slate-11">
            {{ server.url }}
          </span>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Switch
            :model-value="server.enabled"
            @update:model-value="toggleEnabled(server)"
          />
          <Button
            type="button"
            icon="i-lucide-refresh-cw"
            size="sm"
            variant="faded"
            color="slate"
            :is-loading="syncingIds.has(server.id)"
            :label="t('ASSISTANT.TOOLS.MCP.SYNC_BUTTON')"
            @click="syncServer(server)"
          />
          <Button
            type="button"
            icon="i-lucide-trash-2"
            size="sm"
            variant="ghost"
            color="ruby"
            @click="removeServer(server)"
          />
        </div>
      </div>

      <p v-if="server.last_error" class="text-xs text-n-ruby-11">
        {{ t('ASSISTANT.TOOLS.MCP.LAST_ERROR', { error: server.last_error }) }}
      </p>

      <div v-if="server.tools?.length" class="flex flex-wrap gap-1.5">
        <span
          v-for="tool in server.tools"
          :key="tool.name"
          class="px-2 py-0.5 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11"
          :title="tool.description"
        >
          {{ tool.name }}
        </span>
      </div>
      <p v-else class="text-xs text-n-slate-11">
        {{ t('ASSISTANT.TOOLS.MCP.NO_TOOLS') }}
      </p>
    </div>
  </section>
</template>
